// Co je na samolepce: jméno (výchozí, školní samolepky) nebo vlastní nápis (Vánoce, Halloween…).
// Ukládá se do obecného nastavení webu / tématu (textMode, textPresets); každé téma má vlastní.
// textPresets: { cs: [...], pl: [...] } – chybějící jazyk = české položky.
// Tipy pro AI podle tématu řeší lib/aiIdeas.ts (Administrace → Témata).
import { useSyncExternalStore } from 'react';
import { getLang } from './i18n';
import { useLang } from './langSettings';
import { getSiteTheme } from './settings';

// Výchozí hotové nápisy podle adresy/názvu tématu (když je v administraci nevyplníte).
const DEFAULT_PRESETS: { re: RegExp; cs: string[] }[] = [
  { re: /v[aá]no|christ|xmas|weihn|navid|natal|kar[aá]cs|swi[eę]t/i, cs: ['Veselé Vánoce', 'PF 2027', 'Šťastné a veselé', 'Pro tebe', 'Od Ježíška', 'Krásné svátky'] },
  { re: /hallow|dýn|dyn[eě]|strasid|strašid/i, cs: ['Happy Halloween', 'Bubububu', 'Koledu, nebo vyvedu', 'Strašidelný večer', 'Sladkosti, prosím'] },
  { re: /velikon|easter|ostern|wielkanoc|h[uú]sv[eé]t/i, cs: ['Veselé Velikonoce', 'Hody, hody, doprovody', 'Krásné jaro', 'Pro koledníka'] },
];
const themeDefaults = (): string[] => { const t = getSiteTheme() as any; if (!t) return []; return DEFAULT_PRESETS.find(x => x.re.test(`${t.slug} ${t.name || ''}`))?.cs || []; };

export type TextMode = 'name' | 'phrase';
export type ByLang = Record<string, string[]>;
export type TextSettings = { textMode: TextMode; textPresets: ByLang };

let state: TextSettings = { textMode: 'name', textPresets: {} };
const subs = new Set<() => void>();
let saver: ((key: keyof TextSettings, value: any) => void) | null = null;

const cleanMap = (v: any): ByLang => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return {};
  const out: ByLang = {};
  for (const [k, arr] of Object.entries(v)) if (Array.isArray(arr)) { const a = arr.map(x => String(x).trim()).filter(Boolean); if (a.length) out[k] = a; }
  return out;
};

export const textSettings = {
  get: () => state,
  load(general: any) {
    state = {
      textMode: general?.textMode === 'phrase' ? 'phrase' : 'name',
      textPresets: cleanMap(general?.textPresets),
    };
    subs.forEach(f => f());
  },
  setSaver(fn: typeof saver) { saver = fn; },
  setMode(m: TextMode) { state = { ...state, textMode: m }; subs.forEach(f => f()); saver?.('textMode', m); },
  setList(key: 'textPresets', lang: string, items: string[]) {
    const next = { ...state[key] };
    const a = items.map(s => s.trim()).filter(Boolean).slice(0, 12);
    if (a.length) next[lang] = a; else delete next[lang];
    state = { ...state, [key]: next }; subs.forEach(f => f()); saver?.(key, next);
  },
  subscribe(f: () => void) { subs.add(f); return () => { subs.delete(f); }; },
};

const pick = (m: ByLang, l: string) => m[l] || m.cs || (l === 'cs' ? themeDefaults() : []);

export const useTextSettings = () => useSyncExternalStore(textSettings.subscribe, textSettings.get);

/** Texty pro zákaznickou část podle režimu (české, překládají se slovníkem v DOM). */
export function useTextMode() {
  const s = useTextSettings();
  const l = useLang();
  const phrase = s.textMode === 'phrase';
  return {
    phrase,
    presets: phrase ? pick(s.textPresets, l) : [],
    /** Popisek pole: vlastní popisek z administrace má přednost, výchozí „Jméno…“ se v režimu nápisu změní na „Nápis“. */
    label: (nameLabel: string) => (phrase && (!nameLabel || /^Jméno a př[ií]jmení$/.test(nameLabel)) ? 'Nápis' : nameLabel),
  };
}

export const currentPresets = () => (state.textMode === 'phrase' ? pick(state.textPresets, getLang()) : []);
